import store from '@/store';
import infra from '@/infra';
import Web3 from 'web3';
import router from '@/router';
import { updateEarnedBlid } from '@/infra/strategy/earned-blid';
import { CONNECTION_TYPE } from '@/infra/types/wallets/constants';
import { myDialog } from '@/store/my-dialog';
import { useWeb3Store } from '@/web3-server';
import { useStrategiesStore } from '@/store/perfect-strategies';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import SID, { getSidAddress } from '@siddomains/sidjs';
import { useCurrenciesStore } from '@/store/currencies';
import myToast from '@/store/my-toast';
import { useWalletStore } from '@/web3-server/wallet';
import { WALLET_SESSION_TYPE } from '@/web3-server/wallet/types/connect/constants';
import { bloctoEventControl, getEthProviderForEvents, initWCProvider, subscribeToWalletEvents, unsubscribeFromWalletEvents, } from '@/store/wallet/wallet-events';
import { devError } from '@/utils/devError';
import { DEV_ERROR_CODE } from '@/infra/types/dev-error/constants';
import defaultNetwork from '@/web3-server/wallet/data/cross-chain/default-network';
import linker from '@/web3-server/utils/linker';
import { ROUTE_NAME } from '@/infra/types/page/constants';
import GAManager from '@/utils/ga-manager/ga-manager';
export async function connect(params) {
    const strategiesStore = useStrategiesStore();
    const currenciesStore = useCurrenciesStore();
    const defaultProvider = defaultNetwork.BNB.rpcUrl;
    const web3Store = useWeb3Store();
    const walletStore = useWalletStore();
    const isStrategyPage = router && router.currentRoute && router.currentRoute.value && router.currentRoute.value.name
        ? router.currentRoute.value.name !== ROUTE_NAME.NFT
        : true;
    if (store.state.wallet.isConnected) {
        store.commit('wallet/setConnected', false);
    }
    const bloctoSDK = walletStore.initBloctoSDK();
    walletStore.provider = walletStore.getETHProvider();
    const cryptoPluginProvider = walletStore.getDefaultBrowserExtension();
    const exodusProvider = walletStore.getExodusProvider();
    const WCPluginProvider = await initWCProvider(params && params.WCProvider);
    const walletSession = walletStore.walletSession.value;
    const bloctoSession = walletStore.getBloctoSession();
    let whichWalletToChoose = walletStore.whichWalletToChoose();
    const thereIsProvider = !!(WCPluginProvider ||
        exodusProvider ||
        cryptoPluginProvider ||
        bloctoSession);
    const getEthProviderForEventsWitchBlocto = () => {
        let provider = getEthProviderForEvents({ WCPluginProvider });
        if (provider && 'isBlocto' in provider && provider.isBlocto && bloctoSDK.ethereum) {
            provider = bloctoSDK.ethereum;
        }
        return provider;
    };
    const getProviderForWeb3 = () => {
        return getEthProviderForEventsWitchBlocto() || defaultProvider;
    };
    const web3 = new Web3(getProviderForWeb3());
    web3Store.web3 = web3;
    store.commit('wallet/setWeb3', web3);
    const finish = () => {
        store.commit('wallet/setConnected', true);
    };
    const openPopupAuth = () => {
        myDialog.openConnectWalletWithOptionToClose();
        finish();
    };
    bloctoEventControl(bloctoSDK);
    const BLIDPricePromise = walletStore
        .getAllowedNetworkWithoutProvider()
        .then((proposedNetwork) => {
        return walletStore.initBLIDPriceFromCoinGecko(proposedNetwork && proposedNetwork.response);
    });
    BLIDPricePromise.then((BLIDPrice) => {
        if (isStrategyPage) {
            strategiesStore.generalDataInitForAllStrategies(BLIDPrice);
        }
    });
    if (router.currentRoute.value.query && router.currentRoute.value.query.wallet) {
        const address = router.currentRoute.value.query.wallet;
        web3.eth.defaultAccount = address;
        walletStore.userAddress = address;
        store.commit('wallet/setUser', address);
        infra.wallet.setUserTransactions(address);
        await BLIDPricePromise.then(() => {
            return Promise.all([...strategiesStore.userDataInit(), currenciesStore.updateAll()]).finally(() => {
                myToast.init();
                updateEarnedBlid();
                finish();
            });
        });
    }
    else if (!walletStore.disableAutoLogin.value && thereIsProvider) {
        const getConnectionType = (inaccuracyFound) => {
            if (walletSession && !inaccuracyFound) {
                return walletSession.connectionName;
            }
            else {
                if (whichWalletToChoose.isWalletConnect && !!WCPluginProvider) {
                    return CONNECTION_TYPE.WALLET_CONNECT;
                }
                else if (whichWalletToChoose.isExodus) {
                    return CONNECTION_TYPE.EXODUS;
                }
                else if (whichWalletToChoose.isBlocto) {
                    return CONNECTION_TYPE.BLOCTO;
                }
                else {
                    return CONNECTION_TYPE.METAMASK;
                }
            }
        };
        const connectToAccount = () => {
            const provider = getEthProviderForEventsWitchBlocto();
            if (provider) {
                return provider.request({
                    method: 'eth_requestAccounts',
                });
            }
            else {
                devError(DEV_ERROR_CODE.MISSING_VAR);
                return new Promise((resolve, reject) => reject(undefined));
            }
        };
        const getAccounts = async (chainId) => {
            await web3.eth.getAccounts((error, result) => {
                if (!error && result.length) {
                    const isBSC = chainId ? chainId === 56 : true;
                    walletStore.userAddress = result[0];
                    store.commit('wallet/setUser', result[0]);
                    infra.wallet.setUserTransactions(result[0]);
                    if (isBSC) {
                        walletStore.isLoadingUserName = true;
                        getAccountName(result[0])
                            .then((response) => {
                            if (response && response.name) {
                                walletStore.userName = response.name;
                            }
                        })
                            .finally(() => {
                            walletStore.isLoadingUserName = false;
                        });
                    }
                }
            });
        };
        const getAccountName = (address) => {
            const provider = new Web3.providers.HttpProvider(defaultProvider);
            const sid = new SID({ provider, sidAddress: getSidAddress('56') });
            return sid.getName(address);
        };
        const getChainId = async () => {
            try {
                return web3.eth.net
                    .getId()
                    .then((chainId) => {
                    if (chainId) {
                        store.commit('wallet/setChainId', chainId);
                    }
                    return chainId;
                })
                    .catch((e) => {
                    devError(e);
                    return 0;
                });
            }
            catch (e) {
                devError(e);
                return 0;
            }
        };
        const events = () => {
            if (thereIsProvider) {
                unsubscribeFromWalletEvents(WCPluginProvider);
                subscribeToWalletEvents(WCPluginProvider);
            }
        };
        const addWalletSession = (inaccuracyFound) => {
            const connectionType = getConnectionType(inaccuracyFound);
            let walletSessionType = WALLET_SESSION_TYPE.ETHEREUM_EXTENSION;
            if (connectionType === CONNECTION_TYPE.WALLET_CONNECT) {
                walletSessionType = WALLET_SESSION_TYPE.WALLET_CONNECT;
            }
            else if (connectionType === CONNECTION_TYPE.EXODUS) {
                walletSessionType = WALLET_SESSION_TYPE.EXODUS;
            }
            else if (connectionType === CONNECTION_TYPE.BLOCTO) {
                walletSessionType = WALLET_SESSION_TYPE.BLOCTO;
            }
            walletStore.setWalletSession({
                type: walletSessionType,
                connectionName: connectionType,
            });
        };
        const initConnect = async () => {
            const chainId = await getChainId();
            await getAccounts(chainId);
            events();
            if (walletStore.userAddress) {
                walletStore.getAllowedNetwork().then((response) => {
                    walletStore.network = response && response.response ? response.response : null;
                    strategiesStore.updateStatusAndListOfTokensForStrategies(response.response && response.response.id);
                });
                if (!walletSession) {
                    addWalletSession();
                }
                else {
                    const inaccuracyFound = (walletSession.type === WALLET_SESSION_TYPE.EXODUS && !whichWalletToChoose.isExodus) ||
                        (walletSession.type === WALLET_SESSION_TYPE.BLOCTO && !whichWalletToChoose.isBlocto) ||
                        (walletSession.type === WALLET_SESSION_TYPE.WALLET_CONNECT &&
                            !whichWalletToChoose.isWalletConnect) ||
                        (walletSession.type === WALLET_SESSION_TYPE.ETHEREUM_EXTENSION &&
                            (whichWalletToChoose.isBlocto || whichWalletToChoose.isWalletConnect));
                    if (inaccuracyFound) {
                        addWalletSession(inaccuracyFound);
                    }
                }
                await BLIDPricePromise.then(() => {
                    return Promise.all(isStrategyPage
                        ? [
                            ...strategiesStore.userDataInit(),
                            currenciesStore.updateAll(linker.getNetworkId(chainId)),
                        ]
                        : []).finally(() => {
                        if (isStrategyPage) {
                            updateEarnedBlid();
                        }
                        myToast.init();
                        finish();
                    });
                });
            }
            else {
                openPopupAuth();
            }
        };
        const setAuthEventForGA = (walletType, userUid, chainId) => {
            const GAValue = {
                walletType,
                chainId: chainId || store.state.wallet.chainId,
            };
            if (process.env.VUE_APP_USER_DEBUG && process.env.VUE_APP_USER_DEBUG === 'true' && userUid) {
                GAValue.uid = 'w' + userUid;
            }
            GAManager.sendEvent('auth_start_auto_success', {
                valueWithParams: GAValue,
            });
        };
        GAManager.sendEvent('auth_start_auto');
        if ((exodusProvider || cryptoPluginProvider || bloctoSession) && !WCPluginProvider) {
            let connectionResult = null;
            const connectionTimer = setTimeout(() => {
                if (!connectionResult && !store.state.wallet.isConnected) {
                    finish();
                }
            }, 3000);
            connectionResult = await connectToAccount()
                .then((response) => {
                if (bloctoSession) {
                    whichWalletToChoose = walletStore.whichWalletToChoose();
                    walletStore.provider = walletStore.getETHProvider();
                }
                store.commit('wallet/setConnectionType', getConnectionType());
                clearTimeout(connectionTimer);
                walletStore.autoConnectToLiFi(bloctoSDK).then((wallet) => {
                    if (!wallet || !wallet.account) {
                        walletStore.connectToLiFi();
                    }
                });
                return response;
            })
                .catch(() => {
                if (walletStore.getBloctoNetwork()) {
                    walletStore.removeBloctoNetwork();
                }
                GAManager.sendEvent('auth_start_auto_error');
                finish();
            });
            if (Array.isArray(connectionResult)) {
                await initConnect();
                setAuthEventForGA(getConnectionType(), connectionResult.length ? connectionResult[0] : '');
            }
            else {
                finish();
            }
        }
        else {
            setAuthEventForGA(CONNECTION_TYPE.WALLET_CONNECT, WCPluginProvider?.accounts && WCPluginProvider.accounts.length
                ? WCPluginProvider.accounts[0]
                : '', WCPluginProvider?.chainId);
            if (store.state.wallet.connectionType === null) {
                store.commit('wallet/setConnectionType', CONNECTION_TYPE.WALLET_CONNECT);
            }
            walletStore.autoConnectToLiFi().then((wallet) => {
                if (!wallet || !wallet.account) {
                    walletStore.connectToLiFi();
                }
            });
            await initConnect();
        }
    }
    else {
        store.commit('wallet/setConnectionType', null);
        finish();
    }
}
